import React, { useCallback, useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';

const fields = ['name', 'companyName', 'phone', 'contactEmail', 'isActive', 'isPractice'];
const valid = (row, id) =>
  row?._id === id &&
  /^[a-f\d]{64}$/.test(row.revision) &&
  fields.slice(0, 4).every((key) => typeof row[key] === 'string');
export default function AdminCustomerProfile({
  distributorId,
  clinicId,
  onBusyChange,
  onSaved,
  readOnly,
  t,
}) {
  const { begin, version } = useShippingRequest(`${distributorId}:${clinicId}:profile`);
  const [saved, setSaved] = useState(null),
    [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const pending = useRef(false);
  const dirty = !!draft && fields.some((key) => draft[key] !== saved?.[key]);
  const load = useCallback(async () => {
    const request = begin();
    setError('');
    try {
      const row = (await distributorService.adminCustomerProfile(distributorId, clinicId))?.data
        ?.data;
      if (!valid(row, clinicId)) throw new Error('Invalid customer');
      if (request.isCurrent()) {
        setSaved(row);
        setDraft(row);
      }
    } catch {
      if (request.isCurrent()) setError('Could not load customer details. Please try again.');
    }
  }, [begin, distributorId, clinicId]);
  useEffect(() => {
    setSaved(null);
    setDraft(null);
    setNotice('');
    load();
  }, [load, version]);
  useEffect(() => {
    onBusyChange(busy || dirty);
    return () => onBusyChange(false);
  }, [busy, dirty, onBusyChange]);
  const submit = async (event) => {
    event.preventDefault();
    if (pending.current || readOnly || !dirty || !saved) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    const request = begin();
    const changes = Object.fromEntries(
      fields
        .filter((key) => draft[key] !== saved[key])
        .map((key) => [key, typeof draft[key] === 'string' ? draft[key].trim() : draft[key]])
    );
    let receipt;
    try {
      try {
        receipt = (
          await distributorService.saveAdminCustomerProfile(distributorId, clinicId, {
            expectedRevision: saved.revision,
            changes,
          })
        )?.data?.data;
      } catch {
        receipt = (await distributorService.adminCustomerProfile(distributorId, clinicId))?.data
          ?.data;
        if (
          !valid(receipt, clinicId) ||
          !Object.entries(changes).every(
            ([key, value]) =>
              receipt[key] === (key === 'contactEmail' ? value.toLowerCase() : value)
          )
        )
          throw new Error('Unconfirmed save');
      }
      if (
        !valid(receipt, clinicId) ||
        !Object.entries(changes).every(
          ([key, value]) => receipt[key] === (key === 'contactEmail' ? value.toLowerCase() : value)
        )
      )
        throw new Error('Invalid receipt');
      if (!request.isCurrent()) return;
      setSaved(receipt);
      setDraft(receipt);
      setNotice('Customer details saved.');
      await onSaved(receipt);
    } catch {
      if (request.isCurrent())
        setError(
          'Customer details could not be confirmed. Your edits are still here. Reload before saving again.'
        );
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  return (
    <section className="mb-6" aria-label={t('Customer details')}>
      <h3 className="font-semibold mb-3">{t('Customer details')}</h3>
      {error && (
        <p role="alert" className="text-sm text-red-700 mb-3">
          {t(error)}{' '}
          <button type="button" disabled={busy || readOnly} className="underline" onClick={load}>
            {t('Reload customer details')}
          </button>
        </p>
      )}
      {notice && (
        <p role="status" className="text-sm text-green-700 mb-3">
          {t(notice)}
        </p>
      )}
      {!draft && !error && <p role="status">{t('Loading clinics...')}</p>}
      {draft && (
        <form onSubmit={submit}>
          <fieldset disabled={busy || readOnly} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {['name', 'companyName', 'phone', 'contactEmail'].map((key) => (
              <label key={key} className="text-sm text-slate-700">
                {t(
                  {
                    name: 'Customer name',
                    companyName: 'Company name',
                    phone: 'Phone',
                    contactEmail: 'Contact email',
                  }[key]
                )}
                <input
                  className="block w-full mt-1 rounded-lg border px-3 py-2"
                  type={key === 'contactEmail' ? 'email' : key === 'phone' ? 'tel' : 'text'}
                  required={key === 'name'}
                  maxLength={key === 'contactEmail' ? 254 : key === 'phone' ? 40 : 120}
                  value={draft[key]}
                  onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
                />
              </label>
            ))}
            <label className="text-sm flex gap-2 items-center">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(event) => setDraft({ ...draft, isActive: event.target.checked })}
              />
              {t('Active customer')}
            </label>
            <label className="text-sm">
              {t('Practice')}
              <select
                className="block w-full mt-1 rounded-lg border px-3 py-2"
                value={draft.isPractice === null ? '' : String(draft.isPractice)}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    isPractice:
                      event.target.value === '' ? saved.isPractice : event.target.value === 'true',
                  })
                }
              >
                <option value="" disabled>
                  {t('Unreviewed')}
                </option>
                <option value="true">{t('Practice')}</option>
                <option value="false">{t('Hidden')}</option>
              </select>
            </label>
            <p className="sm:col-span-2 text-xs text-slate-500">
              {t('Contact email is separate from login emails and QuickBooks billing email.')}
            </p>
            <div className="sm:col-span-2 flex gap-3 items-center">
              <button
                type="submit"
                disabled={!dirty}
                className="rounded-lg px-4 py-2 bg-[#0089ff] text-white text-sm font-semibold disabled:opacity-40"
              >
                {t(busy ? 'Saving...' : 'Save customer details')}
              </button>
              {dirty && (
                <button
                  type="button"
                  className="text-sm text-[#0089ff]"
                  onClick={() => {
                    setDraft(saved);
                    setError('');
                  }}
                >
                  {t('Discard changes')}
                </button>
              )}
            </div>
          </fieldset>
        </form>
      )}
    </section>
  );
}
